import React from 'react';
import { motion } from 'motion/react';
import { FadeIn } from '@/components/FadeIn';
import { BlurText } from '@/components/ui/animated-blur-text';
import { 
  SiReact, SiVuedotjs, SiNuxtdotjs, SiNextdotjs, SiNodedotjs, 
  SiSpringboot, SiPython, SiTypescript,
  SiFlutter, SiSwift, SiKotlin,
  SiPostgresql, SiMysql, SiRedis, SiMariadb, SiAmazondynamodb,
  SiDocker, SiGooglecloud, SiGithubactions,
  SiFigma, SiTailwindcss,
  SiElectron, SiDotnet
} from 'react-icons/si';
import { FaAws } from 'react-icons/fa';
import { TbBrandAdobe } from 'react-icons/tb';
import { VscCode } from 'react-icons/vsc';
import { Sparkles } from 'lucide-react';

interface TechItem {
  name: string;
  icon: React.ReactNode;
  color: string;
  category: 'frontend' | 'backend' | 'mobile' | 'desktop' | 'database' | 'devops' | 'design';
}

const techStack: TechItem[] = [
  // Frontend
  { name: 'Vue', icon: <SiVuedotjs />, color: '#9CA3AF', category: 'frontend' },
  { name: 'Nuxt', icon: <SiNuxtdotjs />, color: '#9CA3AF', category: 'frontend' },
  { name: 'React', icon: <SiReact />, color: '#9CA3AF', category: 'frontend' },
  { name: 'Next.js', icon: <SiNextdotjs />, color: '#6B7280', category: 'frontend' },
  { name: 'TypeScript', icon: <SiTypescript />, color: '#9CA3AF', category: 'frontend' },
  { name: 'Tailwind', icon: <SiTailwindcss />, color: '#9CA3AF', category: 'frontend' },
  
  // Backend
  { name: 'Node.js', icon: <SiNodedotjs />, color: '#9CA3AF', category: 'backend' },
  { name: 'Spring Boot', icon: <SiSpringboot />, color: '#9CA3AF', category: 'backend' },
  { name: 'Python', icon: <SiPython />, color: '#9CA3AF', category: 'backend' },
  
  // Mobile
  { name: 'React Native', icon: <SiReact />, color: '#9CA3AF', category: 'mobile' },
  { name: 'Flutter', icon: <SiFlutter />, color: '#9CA3AF', category: 'mobile' },
  { name: 'Swift', icon: <SiSwift />, color: '#9CA3AF', category: 'mobile' },
  { name: 'Kotlin', icon: <SiKotlin />, color: '#9CA3AF', category: 'mobile' },
  
  // Desktop
  { name: 'Electron', icon: <SiElectron />, color: '#9CA3AF', category: 'desktop' },
  { name: 'C#', icon: <VscCode />, color: '#9CA3AF', category: 'desktop' },
  { name: '.NET', icon: <SiDotnet />, color: '#9CA3AF', category: 'desktop' },
  
  // Database
  { name: 'PostgreSQL', icon: <SiPostgresql />, color: '#9CA3AF', category: 'database' },
  { name: 'MySQL', icon: <SiMysql />, color: '#9CA3AF', category: 'database' },
  { name: 'MariaDB', icon: <SiMariadb />, color: '#9CA3AF', category: 'database' },
  { name: 'DynamoDB', icon: <SiAmazondynamodb />, color: '#9CA3AF', category: 'database' },
  { name: 'Redis', icon: <SiRedis />, color: '#9CA3AF', category: 'database' },
  
  // DevOps & Cloud
  { name: 'Docker', icon: <SiDocker />, color: '#9CA3AF', category: 'devops' },
  { name: 'AWS', icon: <FaAws />, color: '#9CA3AF', category: 'devops' },
  { name: 'GCP', icon: <SiGooglecloud />, color: '#9CA3AF', category: 'devops' },
  { name: 'CI/CD', icon: <SiGithubactions />, color: '#9CA3AF', category: 'devops' },
  
  // Design
  { name: 'Figma', icon: <SiFigma />, color: '#9CA3AF', category: 'design' },
  { name: 'Adobe XD', icon: <TbBrandAdobe />, color: '#9CA3AF', category: 'design' },
];

const categoryInfo = {
  frontend: { label: 'Frontend', icon: '🎨', gradient: 'from-zinc-800/30 to-zinc-700/30' },
  backend: { label: 'Backend', icon: '⚙️', gradient: 'from-zinc-700/30 to-zinc-800/30' },
  mobile: { label: 'Mobile', icon: '📱', gradient: 'from-zinc-800/30 to-zinc-900/30' },
  desktop: { label: 'Desktop', icon: '🖥️', gradient: 'from-zinc-900/30 to-zinc-800/30' },
  database: { label: 'Database', icon: '💾', gradient: 'from-zinc-700/30 to-zinc-600/30' },
  devops: { label: 'DevOps & Cloud', icon: '☁️', gradient: 'from-zinc-800/30 to-zinc-700/30' },
  design: { label: 'Design', icon: '🎯', gradient: 'from-zinc-700/30 to-zinc-800/30' },
};

export const TechStackSection: React.FC = () => {
  const groupedTech = techStack.reduce((acc, tech) => {
    if (!acc[tech.category]) {
      acc[tech.category] = [];
    }
    acc[tech.category].push(tech);
    return acc;
  }, {} as Record<string, TechItem[]>);

  return (
    <section className="py-20 px-4 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5" />
        <motion.div
          className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <FadeIn className="text-center mb-12">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1, duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-muted/50 backdrop-blur-sm border border-border mb-6"
          >
            <Sparkles className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium text-foreground">
              기술 스택
            </span>
          </motion.div>
          
          <BlurText 
            text="최신 기술로 만드는 차별화된 솔루션"
            className="text-4xl md:text-5xl font-bold mb-4"
            duration={0.8}
            as="h2"
          />
          <p className="text-xl text-foreground/70 max-w-2xl mx-auto">
            다양한 기술 스택을 활용하여 고객님의 요구사항에 최적화된 솔루션을 제공합니다
          </p>
        </FadeIn>

        {/* Tech Stack Grid */}
        <div className="space-y-10">
          {Object.entries(groupedTech).map(([category, techs], categoryIndex) => (
            <FadeIn key={category} delay={categoryIndex * 100}>
              <div className="space-y-6">
                {/* Category Header */}
                <div className="flex items-center gap-4">
                  <span className="text-xl">{categoryInfo[category as keyof typeof categoryInfo].icon}</span>
                  <h3 className="text-xl font-semibold text-foreground">
                    {categoryInfo[category as keyof typeof categoryInfo].label}
                  </h3>
                  <div className="flex-1 h-[1px] bg-border/50" />
                </div>
                
                {/* Tech Icons */}
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                  {techs.map((tech, index) => (
                    <motion.div
                      key={tech.name}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 + index * 0.05 }}
                      whileHover={{ scale: 1.05, y: -5 }}
                      className="group"
                    >
                      <div className={`relative bg-gradient-to-br ${categoryInfo[category as keyof typeof categoryInfo].gradient} backdrop-blur-sm border border-border/50 rounded-2xl p-4 hover:border-primary/30 transition-all duration-300`}>
                        {/* Glow Effect */}
                        <div 
                          className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                          style={{
                            background: `radial-gradient(circle at center, rgba(156, 163, 175, 0.2) 0%, transparent 70%)`,
                            filter: 'blur(20px)'
                          }}
                        />
                        
                        {/* Icon */}
                        <div className="relative flex flex-col items-center gap-3">
                          <div 
                            className="text-3xl transition-all duration-300 group-hover:scale-110 text-zinc-400 group-hover:text-zinc-300"
                          >
                            {tech.icon}
                          </div>
                          <span className="text-sm font-medium text-foreground/80 group-hover:text-foreground transition-colors">
                            {tech.name}
                          </span>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </FadeIn>
          ))}
        </div>

        {/* Bottom Message */}
        <FadeIn delay={600} className="mt-12 text-center">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-muted/30 backdrop-blur-sm border border-border/50"
          >
            <div className="flex -space-x-2">
              {[SiReact, SiVuedotjs, SiNodedotjs, SiDocker].map((Icon, index) => (
                <div
                  key={index}
                  className="w-8 h-8 rounded-full bg-background border-2 border-border flex items-center justify-center"
                >
                  <Icon className="w-4 h-4 text-foreground/60" />
                </div>
              ))}
            </div>
            <span className="text-sm font-medium text-foreground/80 ml-2">
              그 외 다양한 기술 스택 보유
            </span>
          </motion.div>
        </FadeIn>
      </div>
    </section>
  );
};

export default TechStackSection;